import { BarChart3 } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getDashboardActivity } from "../../services/dashboardApi";
import { normalizeApiError } from "../../utils/normalizeApiError";
import "../../styles/dashboard-home.css";

const PAGE_SIZE = 50;

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Full Recent Activity history ("View all" from the dashboard card).
 * Same real sources as the card — employees added, leave requests, processed
 * payroll runs — paged from GET /dashboard/activity. Read-only.
 */
export default function DashboardActivityPage() {
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get("location") || "";

  const query = useInfiniteQuery({
    queryKey: ["dashboard-activity", locationId || "all"],
    queryFn: ({ pageParam = 0 }) =>
      getDashboardActivity({ locationId: locationId || undefined, limit: PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (last) => (last?.has_more ? (last.offset || 0) + (last.limit || PAGE_SIZE) : undefined),
    initialPageParam: 0,
  });

  const items = (query.data?.pages || []).flatMap((page) => page?.items || []);

  return (
    <div className="dash-home">
      <header className="dash-header">
        <div className="dash-greeting">
          <h1>Recent Activity</h1>
          <p>All activity in your organization, newest first.</p>
        </div>
      </header>

      <section className="dash-card" aria-labelledby="activity-all-title">
        <div className="dash-card-head">
          <h2 id="activity-all-title">
            <span className="dash-head-icon" aria-hidden><BarChart3 size={15} /></span>
            Activity History
          </h2>
        </div>
        {query.isLoading ? (
          <p className="dash-empty">Loading activity…</p>
        ) : query.isError ? (
          <div className="dash-section-error" role="alert">
            <span>{normalizeApiError(query.error) || "Unable to load activity."}</span>
            <button type="button" className="dash-link" onClick={() => query.refetch()}>Retry</button>
          </div>
        ) : items.length === 0 ? (
          <p className="dash-empty">No activity yet.</p>
        ) : (
          <>
            {items.map((item, index) => (
              <div className="dash-activity" key={`${item.at}-${index}`}>
                <i className={`dash-dot ${["half", "present", "leave", "absent"][index % 4]}`} aria-hidden />
                <time dateTime={item.at}>{formatWhen(item.at)}</time>
                <b>{item.subject ? `${item.activity} · ${item.subject}` : item.activity}</b>
                <span>{item.location}</span>
              </div>
            ))}
            {query.hasNextPage ? (
              <button
                type="button"
                className="dash-link dash-card-foot"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
